import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { MsalProvider } from "@azure/msal-react";
import { EventType, type AccountInfo } from "@azure/msal-browser";
import { msalInstance } from "./lib/msal";
import { App } from "./App";
import "./styles.css";

async function bootstrap(): Promise<void> {
  const rootElement = document.getElementById("root");
  if (!rootElement) {
    throw new Error("Root element #root not found in index.html");
  }

  // MSAL must finish initialising before React renders anything that might call its APIs.
  await msalInstance.initialize();

  // Critical: explicitly handle the redirect response. When the browser returns
  // from login.microsoftonline.com with a #code=... fragment, this exchanges it
  // for tokens and emits a LOGIN_SUCCESS event. Skipping this leaves the app
  // in "signed out" UI state even though Microsoft completed sign-in.
  try {
    const response = await msalInstance.handleRedirectPromise();
    if (response !== null) {
      // eslint-disable-next-line no-console
      console.info("[auth] handleRedirectPromise — login completed for", response.account?.username);
      if (response.account !== null) {
        msalInstance.setActiveAccount(response.account);
      }
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[auth] handleRedirectPromise failed:", err);
  }

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0 && msalInstance.getActiveAccount() === null) {
    msalInstance.setActiveAccount(accounts[0] ?? null);
  }

  msalInstance.addEventCallback((event) => {
    if (
      event.eventType === EventType.LOGIN_SUCCESS &&
      event.payload !== null &&
      typeof event.payload === "object" &&
      "account" in event.payload
    ) {
      const account = (event.payload as { account: AccountInfo }).account;
      msalInstance.setActiveAccount(account);
    }
  });

  createRoot(rootElement).render(
    <StrictMode>
      <MsalProvider instance={msalInstance}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </MsalProvider>
    </StrictMode>,
  );
}

void bootstrap();
